/**
 * Most Missing Items — "Which items keep going missing?"
 * Adds up every approved count and shows the items that came up SHORT again and
 * again, sorted by how much money they've cost you. Your theft / mis-scan list.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiAlertTriangle, FiTrendingDown, FiPackage, FiDownload, FiPrinter, FiSearch } from 'react-icons/fi';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import { colors } from '../../theme/colors';
import { loadAudits, loadAuditLines } from '../../utils/stockAuditHelper';
import {
  SummaryCards, SummaryCard, SummaryIcon, SummaryInfo, SummaryLabel, SummaryValue,
  FiltersCard, FiltersRow, FilterGroup, Label, Input, Select, ButtonGroup, Button,
  ContentCard, TableContainer, Table, Th, Td, Tr, TotalRow, EmptyState,
  Short, SearchWrap, printReport, downloadCsv, csvCell,
} from './auditReportStyles';

const MostMissingItems = () => {
  const { activeCompany, activeBranch } = useAuth();
  const { formatCurrency } = useCurrency();
  const companyId = activeCompany?.id;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [location, setLocation] = useState('all');
  const [locOptions, setLocOptions] = useState([]);
  const [minTimes, setMinTimes] = useState('1');
  const [itemSearch, setItemSearch] = useState('');

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const audits = (await loadAudits(companyId)).filter(a => a.status === 'Approved');
      setLocOptions([...new Set(audits.map(a => a.locationName).filter(Boolean))].sort());

      const use = audits.filter(a => {
        // Header branch scope — same rule as Inventory Balances.
        if (activeBranch && activeBranch.id !== 'all' && a.branchId !== activeBranch.id) return false;
        const d = String(a.startedAt || '').slice(0, 10);
        if (from && d < from) return false;
        if (to && d > to) return false;
        if (location !== 'all' && a.locationName !== location) return false;
        return true;
      });

      // Pull each count's lines and add up, per item, how often it was short.
      const all = await Promise.all(use.map(a => loadAuditLines(companyId, a.id).catch(() => [])));
      const map = {};
      all.forEach((lines) => {
        lines.forEach(l => {
          const v = Number(l.variance) || 0;
          if (!map[l.itemId]) {
            map[l.itemId] = {
              itemId: l.itemId, itemName: l.itemName || '(unknown)', itemCode: l.itemCode || '',
              timesCounted: 0, timesShort: 0, qtyShort: 0, valueLost: 0,
            };
          }
          const r = map[l.itemId];
          r.timesCounted++;
          if (v < -0.0001) {
            r.timesShort++;
            r.qtyShort += Math.abs(v);
            r.valueLost += Math.abs(v) * (Number(l.unitCost) || 0);
          }
        });
      });

      setItems(Object.values(map)
        .filter(r => r.timesShort > 0)
        .sort((a, b) => b.valueLost - a.valueLost));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [companyId, activeBranch, from, to, location]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    return items.filter(r => {
      if (r.timesShort < Number(minTimes || 1)) return false;
      if (q && !(`${r.itemName} ${r.itemCode}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [items, minTimes, itemSearch]);

  const totals = useMemo(() => rows.reduce((t, r) => {
    t.items++; t.valueLost += r.valueLost; return t;
  }, { items: 0, valueLost: 0 }), [rows]);

  const worst = rows[0] || null;

  const handleExport = () => {
    let csv = 'Item,Code,Times Counted,Times Found Short,Total Qty Short,Total Value Lost\n';
    rows.forEach(r => {
      csv += [
        csvCell(r.itemName), csvCell(r.itemCode),
        r.timesCounted, r.timesShort,
        Math.round(r.qtyShort * 100) / 100, Math.round(r.valueLost * 100) / 100,
      ].join(',') + '\n';
    });
    downloadCsv(`Most_Missing_Items_${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const handlePrint = () => {
    const body = rows.map(r => `<tr>
      <td>${r.itemName}</td><td>${r.itemCode}</td>
      <td class="r">${r.timesCounted}</td>
      <td class="r short">${r.timesShort}</td>
      <td class="r short">${Math.round(r.qtyShort * 100) / 100}</td>
      <td class="r short">${formatCurrency(r.valueLost)}</td>
    </tr>`).join('');
    printReport('Most Missing Items',
      `${activeCompany?.companyName || ''} | ${from || 'All'} to ${to || 'All'} | ${location === 'all' ? 'All Locations' : location}`,
      `<table><thead><tr><th>Item</th><th>Code</th><th class="r">Times Counted</th>
        <th class="r">Times Short</th><th class="r">Total Qty Short</th><th class="r">Total Value Lost</th>
       </tr></thead><tbody>${body}</tbody>
       <tfoot><tr><td colspan="5">Total Value Lost</td>
        <td class="r short">${formatCurrency(totals.valueLost)}</td></tr></tfoot></table>`);
  };

  return (
    <PageLayout pageTitle="Most Missing Items" breadcrumb="Reports / Audit Reports / Most Missing Items">
      <SummaryCards>
        <SummaryCard>
          <SummaryIcon color={colors.error.main}><FiTrendingDown /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Total Value Lost</SummaryLabel>
            <SummaryValue style={{ color: colors.error.main }}>{formatCurrency(totals.valueLost)}</SummaryValue></SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color="#E65100"><FiAlertTriangle /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Worst Item</SummaryLabel>
            <SummaryValue title={worst ? worst.itemName : ''}>{worst ? worst.itemName : '—'}</SummaryValue></SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color={colors.primary.main}><FiPackage /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Items Going Missing</SummaryLabel><SummaryValue>{totals.items}</SummaryValue></SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color="#1565C0"><FiAlertTriangle /></SummaryIcon>
          <SummaryInfo><SummaryLabel>Worst Item Value</SummaryLabel>
            <SummaryValue style={{ color: colors.error.main }}>{worst ? formatCurrency(worst.valueLost) : '—'}</SummaryValue></SummaryInfo>
        </SummaryCard>
      </SummaryCards>

      <FiltersCard>
        <FiltersRow>
          <FilterGroup><Label>From Date</Label><Input type="date" value={from} onChange={e => setFrom(e.target.value)} /></FilterGroup>
          <FilterGroup><Label>To Date</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} /></FilterGroup>
          <FilterGroup>
            <Label>Location</Label>
            <Select value={location} onChange={e => setLocation(e.target.value)}>
              <option value="all">All Locations</option>
              {locOptions.map(l => <option key={l} value={l}>{l}</option>)}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Found Short At Least</Label>
            <Select value={minTimes} onChange={e => setMinTimes(e.target.value)}>
              <option value="1">1 time (all)</option>
              <option value="2">2 times (repeat offenders)</option>
              <option value="3">3 times or more</option>
            </Select>
          </FilterGroup>
          <FilterGroup style={{ minWidth: 220 }}>
            <Label>Search Item</Label>
            <SearchWrap>
              <FiSearch size={14} />
              <Input value={itemSearch} onChange={e => setItemSearch(e.target.value)} placeholder="Item name or code…" />
            </SearchWrap>
          </FilterGroup>
          <ButtonGroup>
            <Button onClick={handleExport} disabled={!rows.length}><FiDownload /> CSV</Button>
            <Button onClick={handlePrint} disabled={!rows.length}><FiPrinter /> Print</Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      <ContentCard>
        <TableContainer>
          {loading ? <EmptyState>Loading…</EmptyState>
            : rows.length === 0 ? <EmptyState>Nothing has gone missing — no item came up short in the approved counts.</EmptyState> : (
              <Table>
                <thead><tr>
                  <Th>Item</Th><Th>Code</Th>
                  <Th align="right">Times Counted</Th><Th align="right">Times Found Short</Th>
                  <Th align="right">Total Qty Short</Th><Th align="right">Total Value Lost</Th>
                </tr></thead>
                <tbody>
                  {rows.map(r => (
                    <Tr key={r.itemId}>
                      <Td><b>{r.itemName}</b></Td>
                      <Td><small>{r.itemCode}</small></Td>
                      <Td align="right">{r.timesCounted}</Td>
                      <Td align="right"><Short>{r.timesShort}</Short></Td>
                      <Td align="right"><Short>{Math.round(r.qtyShort * 100) / 100}</Short></Td>
                      <Td align="right"><Short>{formatCurrency(r.valueLost)}</Short></Td>
                    </Tr>
                  ))}
                </tbody>
                <tfoot>
                  <TotalRow>
                    <Td colSpan={5} style={{ textAlign: 'right' }}>Total Value Lost</Td>
                    <Td align="right"><Short>{formatCurrency(totals.valueLost)}</Short></Td>
                  </TotalRow>
                </tfoot>
              </Table>
            )}
        </TableContainer>
      </ContentCard>
    </PageLayout>
  );
};

export default MostMissingItems;
